import React, { useState, useRef, useEffect } from 'react';
import { Search, X, FileText, User, Wrench, Calendar, AlertCircle, Car, CarIcon, CarFront } from 'lucide-react';
import axios from 'helpers/axios';
import SuperSearchResults from 'models/SuperSearchResultModel';
import config from 'helpers/config';
import CustomerFullName from 'components/common/CustomerFullName';
import history from 'helpers/history';
import { Link } from 'react-router-dom';
import StartingRONumberModal from 'components/pages/jobCentre/jobList/StartingRONumberModal';
import * as RepairOrderHelper from 'helpers/repairOrderHelper';

const SuperSearch = () => {
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const searchRef = useRef<HTMLInputElement>(null);
    const resultsRef = useRef<HTMLDivElement>(null);
    const [isNewRepairOrderLoading, setIsNewRepairOrderLoading] = useState(false);
    const [isStartingNumberModalOpen, setIsStartingNumberModalOpen] = useState(false);
    const [pendingROParams, setPendingROParams] = useState<{ customerId?: string; vehicleId?: string }>();

    const [searchResults, setSearchResults] = useState<SuperSearchResults>();

    const handleSearch = (searchPhrase: string) => {
        if (searchPhrase.length === 0) {
            return;
        }
        axios
            .get(`${config.apiUrl}/misc/search?sq=${searchPhrase}`)
            .then((response) => {
                setSearchResults(response.data);
            })
            .catch((error) => {
                setSearchResults(undefined);
            });
    };

    // const filteredRepairOrders = repairOrders.filter(
    //     (order) =>
    //         order.title.toLowerCase().includes(query.toLowerCase()) ||
    //         order.id.includes(query) ||
    //         order.customer.toLowerCase().includes(query.toLowerCase()),
    // );

    const totalResults =
        (searchResults?.ROs?.count ?? 0) +
        (searchResults?.customers?.count ?? 0) +
        (searchResults?.vehicles?.length ?? 0); //   const getStatusColor = (status) => {
    //     switch (status) {
    //       case 'urgent': return 'text-red-600 bg-red-50';
    //       case 'in-progress': return 'text-blue-600 bg-blue-50';
    //       case 'completed': return 'text-green-600 bg-green-50';
    //       case 'warranty': return 'text-purple-600 bg-purple-50';
    //       default: return 'text-gray-600 bg-gray-50';
    //     }
    //   };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!isOpen) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setSelectedIndex((prev) => (prev < totalResults - 1 ? prev + 1 : prev));
                break;
            case 'ArrowUp':
                e.preventDefault();
                setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
                break;
            case 'Enter':
                e.preventDefault();
                // Handle selection
                break;
            case 'Escape':
                setIsOpen(false);
                setSelectedIndex(-1);
                setIsFocused(false);
                searchRef.current?.blur();
                break;
        }
    };
    const clearSearch = () => {
        setQuery('');
        setSelectedIndex(-1);
        searchRef.current?.focus();
    };

    const focusSearch = () => {
        searchRef.current?.focus();
        setIsOpen(true);
        setIsFocused(true);

        document.getElementById('super-search-input')?.focus();
    };

    const createNewRO = async ({ customerId, vehicleId }: { customerId?: string; vehicleId?: string }) => {
        setIsNewRepairOrderLoading(true);
        setPendingROParams({ customerId, vehicleId });

        await RepairOrderHelper.createAndNavigateToRepairOrder({ customerId, vehicleId }, () => {
            setIsStartingNumberModalOpen(true);
        });

        setQuery('');
        setIsOpen(false);
        setIsNewRepairOrderLoading(false);
    };

    const handleStartingNumberConfirm = async (startingNumber: number) => {
        try {
            setIsNewRepairOrderLoading(true);
            await RepairOrderHelper.updateStartingRONumber(startingNumber);
            setIsStartingNumberModalOpen(false);

            const roId = await RepairOrderHelper.createRepairOrder(pendingROParams || {});
            if (roId) {
                history.push(`/repairOrder/${roId}`);
            }
        } catch (error) {
            console.error('Error creating repair order:', error);
        } finally {
            setIsNewRepairOrderLoading(false);
            setPendingROParams(undefined);
        }
    };
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setIsFocused(false);
            }
        };

        const handleGlobalKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
                event.preventDefault();
                focusSearch();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleGlobalKeyDown);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleGlobalKeyDown);
        };
    }, []);

    return (
        <div className="w-full max-w-2xl mx-auto px-4 py-2">
            <StartingRONumberModal
                isOpen={isStartingNumberModalOpen}
                onClose={() => setIsStartingNumberModalOpen(false)}
                onConfirm={handleStartingNumberConfirm}
            />
            {isNewRepairOrderLoading && (
                <div className="page-loading">
                    <div className="spinner"></div>
                </div>
            )}
            <div className="relative" ref={searchRef}>
                {' '}
                {/* Search Input */}
                <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        id="super-search-input"
                        type="text"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setIsOpen(true);
                            setSelectedIndex(-1);
                            handleSearch(e.target.value);
                        }}
                        onFocus={() => {
                            // setIsOpen(true);
                            // setIsFocused(true);
                        }}
                        onBlur={() => setIsFocused(false)}
                        onKeyDown={handleKeyDown}
                        className="block w-full pl-10 pr-20 py-3 border border-gray-300 rounded-lg bg-white text-sm placeholder-gray-500 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                        placeholder="Search repair orders, customers, or order #..."
                    />
                    {/* Ctrl+K Indicator or Clear Button */}
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                        {query ? (
                            <button onClick={clearSearch} className="hover:text-gray-700 transition-colors">
                                <X className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                            </button>
                        ) : !isFocused ? (
                            <div className="flex items-center space-x-1 px-2 py-1 bg-gray-100 border border-gray-300 rounded text-xs font-medium text-gray-500 shadow-sm">
                                <kbd className="font-mono">Ctrl</kbd>
                                <span>+</span>
                                <kbd className="font-mono">K</kbd>
                            </div>
                        ) : null}
                    </div>
                </div>{' '}
                {/* Search Results Dropdown */}
                {isOpen && query && (isFocused || query.length > 0) && (
                    <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden">
                        <div className="max-h-96 overflow-y-auto">
                            {/* Repair Orders Section */}
                            {(searchResults?.ROs?.rows.length ?? 0) > 0 && (
                                <div className="border-b border-gray-100">
                                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                                        <div className="flex items-center text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                            <Wrench className="h-4 w-4 mr-2" />
                                            Repair Orders ({searchResults?.ROs.count})
                                        </div>
                                    </div>
                                    {searchResults?.ROs.rows.map((RO, index) => (
                                        <Link
                                            key={RO.id}
                                            to={`/repairOrder/${RO.id}`}
                                            onClick={() => {
                                                setIsOpen(false);
                                                clearSearch();
                                            }}
                                        >
                                            <div
                                                key={RO.id}
                                                className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors ${
                                                    selectedIndex === index ? 'bg-blue-50 border-blue-200' : ''
                                                }`}
                                            >
                                                <div className="flex items-start justify-between">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center space-x-2 mb-1">
                                                            <span className="text-sm font-semibold text-green-600">
                                                                #{RO.repairOrderNumber}
                                                            </span>
                                                            {/* <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                              {order.status === 'urgent' && <AlertCircle className="h-3 w-3 mr-1" />}
                              {order.status.replace('-', ' ')}
                            </span> */}
                                                        </div>
                                                        <p className="text-sm text-gray-600 truncate">{RO.title}</p>
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            <CustomerFullName
                                                                customerId={RO.customerId}
                                                                customer={RO.customer}
                                                            />
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}

                            {/* Customers Section */}
                            {(searchResults?.customers?.rows?.length ?? 0) > 0 && (
                                <div>
                                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                                        <div className="flex items-center text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                            <User className="h-4 w-4 mr-2 text-blue-500" />
                                            Customers ({searchResults?.customers?.count})
                                        </div>
                                    </div>
                                    {searchResults?.customers?.rows.map((customer, _) => (
                                        <div
                                            key={customer.id}
                                            className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors 
                                            `}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center space-x-3">
                                                        <div className="flex-shrink-0">
                                                            <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                                                                <User className="h-4 w-4 text-blue-600" />
                                                            </div>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-sm font-semibold text-gray-900">
                                                                <CustomerFullName
                                                                    customerId={customer.id!}
                                                                    customer={customer}
                                                                />
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {/* {customer.phoneNumbers} */}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center  text-gray-500">
                                                    {/* {customer.} */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            createNewRO({ customerId: customer.id! });
                                                        }}
                                                        className="flex items-center space-x-1 px-2 py-1 bg-green-500 hover:bg-green-600 text-white  font-semibold rounded transition-colors"
                                                    >
                                                        <FileText className="h-5 w-5" />
                                                        <span>New RO</span>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Vehicles Section */}
                            {(searchResults?.vehicles?.length ?? 0) > 0 && (
                                <div>
                                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                                        <div className="flex items-center text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                            <CarFront className="h-4 w-4 mr-2 text-green-500" />
                                            Vehicles ({searchResults?.vehicles?.length})
                                        </div>
                                    </div>
                                    {searchResults?.vehicles?.map((vehicle, index) => (
                                        <div
                                            key={vehicle.id}
                                            className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-b-0 transition-colors 
                                            `}
                                        >
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-semibold text-gray-900">
                                                        {vehicle.make} {vehicle.model}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {vehicle.year} - {vehicle.VIN}
                                                    </p>
                                                </div>

                                                <div className="flex items-center  text-gray-500">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            createNewRO({
                                                                vehicleId: vehicle.id,
                                                                customerId: vehicle.customerId,
                                                            });
                                                        }}
                                                        className="flex items-center space-x-1 px-2 py-1 bg-green-500 hover:bg-green-600 text-white  font-semibold rounded transition-colors"
                                                    >
                                                        <FileText className="h-5 w-5" />
                                                        <span>New RO</span>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2 text-xs font-semibold text-gray-600 mt-1">
                                                <CustomerFullName
                                                    customerId={vehicle.customerId!}
                                                    customer={vehicle.customer}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* No Results */}
                            {totalResults === 0 && (
                                <div className="px-4 py-8 text-center">
                                    <Search className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-600">No results found for "{query}"</p>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Try searching by order number, customer name, vehicle details or other keywords.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Quick Actions Footer */}
                        {totalResults > 0 && (
                            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
                                <div className="flex items-center justify-between text-xs text-gray-500">
                                    <span>
                                        {totalResults} result{totalResults !== 1 ? 's' : ''} found
                                    </span>
                                    <div className="flex items-center space-x-4">
                                        <span>↑↓ Navigate</span>
                                        <span>Enter Select</span>
                                        <span>Esc Close</span>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SuperSearch;
